import type { ChannelId } from "@praesto/shared-types";
import { Badge, Chip } from "@praesto/ui-kit";
import { useCore } from "../lib/core";

const CHANNELS: ReadonlyArray<ChannelId> = ["inApp", "email", "push"];

/**
 * Shared presentational cell: a state Badge + 3 toggle Chips (inApp/email/push)
 * + a "usa predefinito" reset. Used by both the tenant-wide (role × type)
 * rules grid (`NotificationRulesSettings`, Task 9) and the personal (type-only)
 * per-type overrides grid (`NotificationsSettings`, Task 10).
 *
 * Purely presentational — model-specific "default"/"custom"/"off" semantics
 * live in each caller's own pure helpers (`rules-model.ts` / `per-type-model.ts`);
 * this component only renders the current `state`/`active` and reports
 * toggle/reset intents back up.
 */
export function ChannelCell(props: {
  readonly state: "default" | "custom" | "off";
  readonly active: ReadonlyArray<ChannelId>;
  readonly onToggle: (channel: ChannelId) => void;
  readonly onReset: () => void;
}) {
  const core = useCore();
  const t = core.i18n.t;
  const { state, active, onToggle, onReset } = props;
  const stateTone = state === "custom" ? "accent" : state === "off" ? "warning" : "neutral";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge tone={stateTone} variant="outline" size="xs">
        {t(`settings.notifications.rules.state.${state}`)}
      </Badge>
      {CHANNELS.map((c) => {
        const on = active.includes(c);
        return (
          <button
            key={c}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(c)}
            className="cursor-pointer rounded focus-visible:outline-none focus-visible:shadow-focus min-h-touch sm:min-h-0"
          >
            {/* v18: a selected chip fills with the theme ink; state colors
                belong to Badges, not to the selection itself. */}
            <Chip selected={on} size="xs">
              {t(`settings.notifications.rules.channel.${c}`)}
            </Chip>
          </button>
        );
      })}
      {state !== "default" && (
        <button
          type="button"
          onClick={onReset}
          className="text-caption text-fg-muted hover:underline min-h-touch px-1 cursor-pointer"
        >
          {t("settings.notifications.rules.reset")}
        </button>
      )}
    </div>
  );
}
